import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { debounce } from 'lodash';
import axiosInstant from '../../../api';
import ShortlistsHeader from '../Shortlists/ShortlistsHeader';
import IconSearch from '../../../assets/svg/search-icon-grey.svg';
import VideoShortlistCard from './VideoShortlistCard';
import ShortlistFormModal from '../Shortlists/ShortlistFormModal';
import { MainContainer } from '../../../components/ContentWrapper';
import { DotLottleLoader } from '../../../components/Loader/DotLottleLoader';
import { LoadMoreBtn, PageDivider } from '../../../components/Loader';
import { NoDataView } from '../../../components/EmptyPanel/NoDataView';
import { IconVideo } from '../../../utils/icons';
import { useCommonParams } from '../../../hooks/useCommonParams';
import { usePagination } from '../../../hooks/usePagination';
import { videoShortlistsEndpoint } from '../../../api/endpoints';
import SortControls from '../../../components/SortControls/SortControls';

const PAGE_SIZE = 20;

const VideoShortlists = () => {
  type VideoShortlist = any;

  const {
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    page,
    setPage,
    sortType,
    setSortDirection,
    setSortType,
  } = useCommonParams({ defaultSortKey: 'created_at', defaultSortAscending: false });

  const [open, setOpen] = useState(false);
  const [localSearchInput, setLocalSearchInput] = useState<string>(
    searchParams.get('search') || '',
  );

  const debouncedSetSearchRef = useRef<ReturnType<typeof debounce> | null>(null);

  const fetchFn = useCallback(
    async (pageNum: number) => {
      try {
        const sortParam = `${sortType.isAscending ? '' : '-'}${sortType.key}`;
        const response = await axiosInstant.get(videoShortlistsEndpoint, {
          params: {
            search: searchText || undefined,
            page: pageNum,
            limit: PAGE_SIZE,
            sort: sortParam,
          },
        });

        const rows = response.data.rows || [];
        const count = response.data.count || 0;
        return { rows, count, error: null };
      } catch (e: any) {
        if (e?.name === 'CanceledError' || e?.name === 'AbortError') {
          return { rows: [], count: 0, error: null };
        }
        return { rows: [], count: 0, error: e?.message || 'Failed to load video shortlists' };
      }
    },
    [searchText, sortType.key, sortType.isAscending],
  );

  const {
    items: shortlists,
    totalCount: totalShortlists,
    loading: isLoading,
    hasMore,
    canLoadPrevious,
    loadingPrevious,
    initLoad,
    loadMore,
    loadPrevious,
    topSentinelRef,
  } = usePagination<VideoShortlist>({
    fetchFn,
    pageSize: PAGE_SIZE,
    deepLink: {
      page,
      setPage,
      searchParams,
      setSearchParams,
      ignoreParams: ['page'],
    },
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setLocalSearchInput(newSearchValue);
    debouncedSetSearchRef.current?.(newSearchValue);
  };

  useEffect(() => {
    debouncedSetSearchRef.current = debounce((value: string) => {
      setSearchText(value);
    }, 300);

    return () => {
      debouncedSetSearchRef.current?.cancel();
      debouncedSetSearchRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalSearchInput(searchText || '');
  }, [searchText]);


  return (
    <MainContainer>
      <Box className='flex flex-col gap-6'>
        <ShortlistsHeader
          setOpen={setOpen}
          title="Video Shortlists"
          buttonLabel="New Video Shortlist"
        />

        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
          <div className='relative w-full sm:w-auto'>
            <img
              className='absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5'
              src={IconSearch}
              alt='search'
            />
            <input
              type='text'
              value={localSearchInput}
              onChange={handleSearchChange}
              className='w-full sm:w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1'
              placeholder='Search'
            />
          </div>
          <SortControls
            sortKey={sortType.key}
            isAscending={sortType.isAscending}
            onDirectionChange={setSortDirection}
            onSortKeyChange={(key) => setSortType({ key, isAscending: sortType.isAscending })}
            showSelect
            options={[
              { value: 'created_at', label: 'Created Date' },
              { value: 'name', label: 'Name' },
              { value: 'id', label: 'ID Number' },
            ]}
          />
        </div>

        <PageDivider
          count={totalShortlists}
          label="Video Shortlists"
          className=""
          canLoadPrevious={canLoadPrevious}
          hasMore={hasMore}
          onLoadPrevious={loadPrevious}
        />

        {isLoading && shortlists.length === 0 ? (
          <div className='flex justify-center items-center py-20'>
            <DotLottleLoader fill={false} />
          </div>
        ) : shortlists.length === 0 ? (
          <NoDataView
            text='No Video Shortlists found'
            subText='Your search does not match any existing video shortlists.'
            Icon={IconVideo}
            iconProps={{ shape: 'square' }}
          />
        ) : (
          <>
            {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
            {loadingPrevious && (
              <div className="flex justify-center py-2">
                <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
              </div>
            )}
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
              {shortlists.map((shortlist: VideoShortlist) => (
                <VideoShortlistCard
                  key={shortlist?.id}
                  shortlist={shortlist}
                  fetchShortlists={() => initLoad(1)}
                />
              ))}
            </div>
          </>
        )}

        {shortlists.length > 0 && hasMore && (
          <LoadMoreBtn onClick={loadMore} loading={isLoading} customClasses="!my-4" />
        )}

        <ShortlistFormModal
          mode="create"
          open={open}
          setOpen={setOpen}
          onShortlistCreated={() => initLoad(1)}
          apiBase="/video-shortlists"
          navigateTo="/videos/shortlists"
          label="Video Shortlist"
        />
      </Box>
    </MainContainer>
  );
};

export default VideoShortlists;
